import {
    BezierCurve03,
    Building01,
    Building07,
    Calendar,
    CalendarDate,
    ClockFastForward,
    File02,
    FileCheck03,
    Home05,
    Package,
    PackageX,
    Snowflake02,
    ThermometerWarm,
    Truck01,
    Truck02,
    User01,
    User03,
    Tool02,
} from '@untitled-ui/icons-react/build/cjs';
import React from 'react';
import { BadgeType } from 'src/interface/common';

export const APP_FULL_NAME: string = window.FULL_NAME as string;
export const TIMEZONE: any = window.TIMEZONE;
export const DATE_FORMAT: string = 'MMM DD, YYYY';

export const FOOT = 5;

export const BOL_LIMIT_BY_CARRIER: Record<string, number> = {
    FREIGHTCOM: 30,
};

export const REGULAR_EXPRESSION_NEW = {
    EMAIL: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,15}$/,
    TEXT: (maxChars: number) => new RegExp(`^.{1,${maxChars}}$`),
    DIGIT_DECIMAL: (maxDigits: number) => {
        const intPart = `[1-9]\\d{0,${maxDigits - 1}}`;
        const decimalPart = `(\\.\\d{0,2})?`;
        const zeroCase = `0(\\.\\d{0,2})?`;
        const pattern = `^(${zeroCase}|${intPart}${decimalPart})$`;

        return new RegExp(pattern);
    },
    DIGIT_DECIMAL_WITH_NEGATIVE: (maxDigits: number) => {
        const intPart = `[1-9]\\d{0,${maxDigits - 1}}`;
        const decimalPart = `(\\.\\d{0,2})?`;
        const zeroCase = `0(\\.\\d{0,2})?`;

        const validNumbers = `(${zeroCase}|${intPart}${decimalPart})`;

        const loneMinusSign = '|-';
        const partialDecimal = `|(0|[1-9]\\d{0,${maxDigits - 1}})\\.`;

        const pattern = `^(${validNumbers}|-${validNumbers}${loneMinusSign}${partialDecimal})$`;

        return new RegExp(pattern);
    },
    DIGIT_ABSOLUTE: (maxDigits: number, allowZeroToEnter: boolean = false) => {
        const secondPartMaxDigits = maxDigits - 1;

        // Determine the allowed range for the first digit
        const firstDigitRange = allowZeroToEnter ? '[0-9]' : '[1-9]';

        const pattern = `^(0|${firstDigitRange}\\d{0,${secondPartMaxDigits}})$`;

        return new RegExp(pattern);
    },
    DIGIT_ABSOLUTE_WITH_NEGATIVE: (maxDigits: number) => {
        const secondPartMaxDigits = maxDigits - 1;
        const validNumbers = `(0|[1-9]\\d{0,${secondPartMaxDigits}})`;
        const loneMinusSign = '|-';
        const pattern = `^(${validNumbers}|-${validNumbers}${loneMinusSign})$`;

        return new RegExp(pattern);
    },
};

export const LBS = {
    WEIGHT: 6000,
    LENGTH: 144, // Inches
    WIDTH: 96, // Inches
    HEIGHT: 96, // Inches
    MAX_WEIGHT: 2500,
};

export const LBS_MAX_VALUES = {
    LENGTH: 144, // itemLength
    WIDTH: 96,
    HEIGHT: 96,
    MAX_WEIGHT: 100000, // totalWeight
    ITEM_LENGTH_OVER: FOOT * 12,
    TAILGATE_MAX_WEIGHT: 2500,
};

export const KGS_MAX_VALUES = {
    LENGTH: Math.ceil(LBS_MAX_VALUES.LENGTH / 0.3937), // itemLength
    WIDTH: Math.ceil(LBS_MAX_VALUES.WIDTH / 0.3937),
    HEIGHT: Math.ceil(LBS_MAX_VALUES.HEIGHT / 0.3937),
    MAX_WEIGHT: Math.ceil(LBS_MAX_VALUES.MAX_WEIGHT / 2.2046), // totalWeight
    ITEM_LENGTH_OVER: Math.ceil(FOOT * 30.48),
    TAILGATE_MAX_WEIGHT: Math.ceil(LBS.MAX_WEIGHT / 2.2046),
};

export const EDIT_QUOTE_TABS = {
    SHIPPER_DETAILS: 'shipper_details',
    CONSIGNEE_DETAILS: 'consignee_details',
    SHIPMENT_DETAILS: 'shipment_details',
    DIMENSIONS: 'dimensions',
    PICKUPS_DROPOFFS: 'pickups_dropoffs',
};

export const QUOTE_URGENCY = {
    LIVE: 'live',
    QFR: 'qfr',
};

export const MEASUREMENT = {
    WEIGHT1: {
        value: 'LBS',
        label: 'LBS',
    },
    WEIGHT2: {
        value: 'KGS',
        label: 'KGS',
    },
    LENGTH1: {
        value: 'INCHES',
        label: 'IN',
    },
    LENGTH2: {
        value: 'CM',
        label: 'CM',
    },
};

export const KGS = {
    WEIGHT: Math.ceil(LBS.WEIGHT / 2.2046),
    LENGTH: Math.ceil(LBS.LENGTH / 0.3937), // CM
    WIDTH: Math.ceil(LBS.WIDTH / 0.3937), // CM
    HEIGHT: Math.ceil(LBS.HEIGHT / 0.3937), // CM
    ITEM_LENGTH_OVER: Math.ceil(FOOT * 30.48), // CM
    MAX_WEIGHT: Math.ceil(LBS.MAX_WEIGHT / 2.2046),
};

export const STATUS = {
    PENDING: 'Pending',
    OPENED: 'Opened',
    ACTIVE: 'Active',
    TO_BOOK: 'To-Book',
    WON: 'Won',
    LOST: 'Lost',
    EXPIRED: 'Expired',
    QUOTE_REQUEST: 'QuoteRequest',
    BOOKING_REQUEST: 'BookingRequested',
    BOOKED: 'Booked',
    ALL: 'All',
    REQUESTED: 'Requested',
};

export const CURRENCY = {
    USD: 'USD',
    CAD: 'CAD',
};

export const USER_ROLE = {
    ADMIN: 'admin',
    USER: 'user',
};

export const PERMISSION = {
    VIEW_SMART_QUOTE: 'view_smart_quote',
    RADAR: 'radar',
};

export const TABLE_IDS = {
    QUOTE_LIST: 'quoteList',
    CUSTOMER_LIST: 'customerList',
    TARGET_LIST: 'targetList',
    CUSTOMER_DETAILS: 'customerDetail',
    ORDER_LIST: 'orderList',
    RADAR_LIST: 'radarList',
};

export const QUOTE_CLASSIFICATION = {
    LTL: 'ltl',
    FTL: 'ftl',
    VOLUME_LTL: 'volume_ltl',
};

export const VIEW_NAME_MAX_LENGTH = 40;
export const MAP_API: string = 'google_map';
export const TRIMBLE_KEY = '707CC148BE16B64A8102C686C8B8DBF3';

export const TimeRanges = [
    { label: 'Last 7 days', value: 'last_7_days', key: 'last_7_days' },
    { label: 'Last 30 days', value: 'last_30_days', key: 'last_30_days' },
    { label: 'Last 90 days', value: 'last_90_days', key: 'last_90_days' },
    { label: 'Last Year', value: 'last_year', key: 'last_year' },
    { label: 'Weekly', value: 'weekly', key: 'weekly' },
    { label: 'Monthly', value: 'monthly', key: 'monthly' },
    { label: 'Quarterly', value: 'quarterly', key: 'quarterly' },
    { label: 'Yearly', value: 'yearly', key: 'yearly' },
    { label: 'All Time', value: 'all_time', key: 'all_time' },
    { label: 'Custom', value: 'custom', key: 'custom' },
];

export const currencyList = [
    { label: 'CAD ($)', value: CURRENCY.CAD, icon: React.createElement('span', null, '🇨🇦') },
    { label: 'USD ($)', value: CURRENCY.USD, icon: React.createElement('span', null, '🇺🇸') },
];

const STATUS_TO_BADGE_TYPE: Record<string, BadgeType> = {
    [STATUS.WON]: 'success',
    [STATUS.ACTIVE]: 'lightblue',
    [STATUS.OPENED]: 'primary',
    [STATUS.EXPIRED]: 'error',
    [STATUS.LOST]: 'error',
    DEFAULT: 'success',
};

const STATUS_TO_BADGE_LABEL: Record<string, string> = {
    [STATUS.WON]: 'Won',
    [STATUS.EXPIRED]: 'Expired',
    [STATUS.LOST]: 'Lost',
    [STATUS.ACTIVE]: 'Quoted',
    [STATUS.OPENED]: 'Requested',
    [STATUS.REQUESTED]: 'Booked',
};

export const getBadgeTypeByQuoteStatus = (status: string): BadgeType => STATUS_TO_BADGE_TYPE[status] || STATUS_TO_BADGE_TYPE.DEFAULT;

export const getBadgeLabelByQuoteStatus = (status: string): string => STATUS_TO_BADGE_LABEL[status] || status;

export const ORDER_STATUS = {
    NEW: 'new',
    QUOTED: 'quoted',
    EXPIRED: 'expired',
    CANCELLED: 'cancelled',
    BOOKED: 'booked',
    BOOKING_IN_PROGRESS: 'booking_in_progress',
    BOOKING_FAILED: 'booking_failed',
    INVOICE_PAID: 'invoice-paid',
    DELIVERED: 'delivered',
    INVOICE_CREATED: 'invoice-created',
    INVOICE_SENT: 'invoice-sent',
    DISPATCHED: 'dispatched',
    IN_TRANSIT: 'in-transit',
    COMPLETED: 'completed',
    DRAFT: 'draft',
};

/**
 * Statuses for which the portal still offers "Share Tracking Link".
 *
 * The link's lifetime is the server's answer, not this list's: it is valid for the life of the
 * shipment plus 7 days after delivery (wal-customer-portal-be src/utils/shareLinkExpiry.ts), and
 * the order-detail response carries `isShareLinkExpired` for exactly that boundary. This list only
 * answers "does this status have a customer tracking page at all".
 *
 * The billing statuses are here on purpose: orders move from delivered to completed/invoiced within
 * hours, so without them the button would vanish long before the link actually expires.
 * cancelled / draft are excluded — the public lookup returns "not found" for those, so there is no
 * page to share. Mirrors wal-frontend-v2's SHARE_TRACKING_LINK_STATUSES; change both or neither.
 */
export const SHARE_TRACKING_LINK_STATUSES: string[] = [
    ORDER_STATUS.DISPATCHED,
    ORDER_STATUS.IN_TRANSIT,
    ORDER_STATUS.DELIVERED,
    ORDER_STATUS.COMPLETED,
    ORDER_STATUS.INVOICE_CREATED,
    ORDER_STATUS.INVOICE_SENT,
    ORDER_STATUS.INVOICE_PAID,
];

export const iconMap: any = {
    'Tailgate Pickup': {
        icon: Truck01,
        className: 'w-4 h-4 flex-none text-utilityGray800',
    },
    'Pickup Appointment Required': {
        icon: Calendar,
        className: 'w-4 h-4 flex-none text-utilityGray800',
    },
    'Residential Pickup': {
        icon: Home05,
        className: 'w-4 h-4 flex-none text-utilityGray800',
    },
    'Trade Show Pickup': {
        icon: Building07,
        className: 'w-4 h-4 flex-none text-utilityGray800',
    },
    'Limited Access Pickup': {
        icon: BezierCurve03,
        className: 'w-4 h-4 flex-none text-utilityGray800',
    },
    'Threshold Pickup': {
        icon: Building01,
        className: 'w-4 h-4 flex-none text-utilityGray800',
    },
    'After Hours / Weekend / Holiday Pickup': {
        icon: CalendarDate,
        className: 'w-4 h-4 flex-none text-utilityGray800',
    },
    'Hold For Pickup': {
        icon: ClockFastForward,
        className: 'w-4 h-4 flex-none text-utilityGray800',
    },

    'Tailgate Delivery': {
        icon: Truck01,
        className: 'w-4 h-4 flex-none text-utilityGray800',
    },
    'Residential Delivery': {
        icon: Home05,
        className: 'w-4 h-4 flex-none text-utilityGray800',
    },
    'Trade Show Delivery': {
        icon: Building07,
        className: 'w-4 h-4 flex-none text-utilityGray800',
    },
    'Limited Access Delivery': {
        icon: BezierCurve03,
        className: 'w-4 h-4 flex-none text-utilityGray800',
    },
    'Delivery Appointment Required': {
        icon: Calendar,
        className: 'w-4 h-4 flex-none text-utilityGray800',
    },
    'Threshold Delivery': {
        icon: Building01,
        className: 'w-4 h-4 flex-none text-utilityGray800',
    },
    'Handbomb Delivery': {
        icon: ClockFastForward,
        className: 'w-4 h-4 flex-none text-utilityGray800',
    },

    'Protect From Freeze': {
        icon: ThermometerWarm,
        className: 'w-4 h-4 flex-none text-utilityGray800',
    },
    'In-bond': {
        icon: FileCheck03,
        className: 'w-4 h-4 flex-none text-utilityGray800',
    },
    'Dangerous Goods': {
        icon: PackageX,
        className: 'w-4 h-4 flex-none text-utilityGray800',
    },
    Tarps: {
        icon: BezierCurve03,
        className: 'w-4 h-4 flex-none text-utilityGray800',
    },
    Vented: {
        icon: Calendar,
        className: 'w-4 h-4 flex-none text-utilityGray800',
    },
    'Tri-axle': {
        icon: Truck02,
        className: 'w-4 h-4 flex-none text-utilityGray800',
    },
    Team: {
        icon: User01,
        className: 'w-4 h-4 flex-none text-utilityGray800',
    },
    Frozen: {
        icon: Snowflake02,
        className: 'w-4 h-4 flex-none text-utilityGray800',
    },
    Expedite: {
        icon: ClockFastForward,
        className: 'w-4 h-4 flex-none text-utilityGray800',
    },
    'Load Insurance': {
        icon: File02,
        className: 'w-4 h-4 flex-none text-utilityGray800',
    },
    'Flatbed Pickup': {
        icon: Package,
        className: 'w-4 h-4 flex-none text-utilityGray800',
    },
    'Flatbed Delivery': {
        icon: Package,
        className: 'w-4 h-4 flex-none text-utilityGray800',
    },
    'Lumper Service': {
        icon: Truck01,
        className: 'w-4 h-4 flex-none text-utilityGray800',
    },
    'Driver Assist': {
        icon: User03,
        className: 'w-4 h-4 flex-none text-utilityGray800',
    },
    'After Hours Delivery': {
        icon: CalendarDate,
        className: 'w-4 h-4 flex-none text-utilityGray800',
    },
    'Construction Site Delivery': {
        icon: Tool02,
        className: 'w-4 h-4 flex-none text-utilityGray800',
    },
};

export const COMMON_CONSTANTS = {
    DYNAMIC_CREATE_LABEL_TEXT: '{{DYNAMIC_INPUT}}',
};

// Dynamic badge configuration for classification types
export const CLASSIFICATION_BADGE_CONFIG = {
    [QUOTE_CLASSIFICATION.LTL]: {
        type: 'success',
        label: 'QuotingHub.Classification.LTL',
    },
    [QUOTE_CLASSIFICATION.FTL]: {
        type: 'lightblue',
        label: 'QuotingHub.Classification.FTL',
    },
    [QUOTE_CLASSIFICATION.VOLUME_LTL]: {
        type: 'pink',
        label: 'QuotingHub.Classification.Volume_LTL',
    },
} as const;

const ORDER_STATUS_LABELS = {
    [ORDER_STATUS.NEW]: 'New',
    [ORDER_STATUS.QUOTED]: 'Quoted',
    [ORDER_STATUS.EXPIRED]: 'Expired',
    [ORDER_STATUS.CANCELLED]: 'Cancelled',
    [ORDER_STATUS.BOOKED]: 'Booked',
    [ORDER_STATUS.BOOKING_IN_PROGRESS]: 'Booking In Progress',
    [ORDER_STATUS.BOOKING_FAILED]: 'Booking Failed',
    [ORDER_STATUS.INVOICE_PAID]: 'Invoice Paid',
    [ORDER_STATUS.DELIVERED]: 'Delivered',
    [ORDER_STATUS.INVOICE_CREATED]: 'Invoice Created',
    [ORDER_STATUS.INVOICE_SENT]: 'Invoice Sent',
    [ORDER_STATUS.DISPATCHED]: 'Dispatched',
    [ORDER_STATUS.IN_TRANSIT]: 'In Transit',
    [ORDER_STATUS.COMPLETED]: 'Completed',
    [ORDER_STATUS.DRAFT]: 'Drafted',
};

export const getBadgeLabelByOrderStatus = (status: string): string => ORDER_STATUS_LABELS[status] || status;

export const MAX_LENGTH = {
    EXTENSION: 6,
    PHONE_NUMBER: 15,
    EAPNBR: 20,
    EMAIL: 100,
    COMMODITY: 50,
};

export const CANADIAN_AREA_CODES = [
    '204',
    '226',
    '236',
    '249',
    '250',
    '289',
    '306',
    '343',
    '365',
    '403',
    '416',
    '418',
    '431',
    '437',
    '438',
    '450',
    '506',
    '514',
    '519',
    '548',
    '579',
    '581',
    '587',
    '604',
    '613',
    '639',
    '647',
    '672',
    '705',
    '709',
    '742',
    '778',
    '780',
    '782',
    '807',
    '819',
    '825',
    '867',
    '873',
    '902',
    '905',
];

export const currencyOptions = [
    {
        label: 'CAD',
        value: 'CAD',
    },
    {
        label: 'USD',
        value: 'USD',
    },
];

export const getOrderStatusBadgeType = (status: any) => {
    let type: BadgeType = 'success';

    if (status === 'draft' || status === 'cancelled' || status === 'dispatched') {
        type = 'pink';
    } else if (status === 'booked' || status === 'delivered') {
        type = 'lightblue';
    } else if (status === 'in-transit' || status === 'booking_failed') {
        type = 'error';
    } else if (status === 'completed') {
        type = 'gray';
    } else {
        type = 'red';
    }

    return type;
};

export const inbondTypeList = [
    {
        name: '(IT) Immediate Transportation Bond',
        value: 'immediate-exportation',
    },
    {
        name: '(T&E) Transportation & Export Bond',
        value: 'transportation-and-exportation',
    },
];

export const inbondContactMethodList = [
    { name: 'Email', value: 'email-address' },
    { name: 'Phone', value: 'phone-number' },
    { name: 'Fax Number', value: 'fax-number' },
];

export const DIMENSION_REQUIRED_EQUIPMENT_TYPES = ['van', 'dry van', 'reefer', 'straight truck'];

// Vehicle type codes that require line item dimensions
const DIMENSION_REQUIRED_EQUIPMENT_CODES = ['V', 'R', 'K']; // Dry Van, Reefer, Straight Truck

export const FLATBED_EQUIPMENT_CODE = 'F';

// Step Deck (S) and LowBoy/RGN (L) — both share the step-deck stacking ceiling of 129".
const STEP_DECK_EQUIPMENT_CODES = ['S', 'L'];

// STANDARD_VAN = Dry Van / Reefer / Straight Truck. Flatbed > 102" height requires step deck equipment.
// STEP_DECK caps cover Step Deck + LowBoy/RGN.
// OTHER applies to any equipment type outside the named groups (Container, Power Only, etc.) — defaults to closed-deck-equivalent cap (110").
const EQUIPMENT_DIMENSION_CAPS_INCHES = {
    STANDARD_VAN: { HEIGHT: 110 as number | null, WIDTH: 98 as number | null },
    FLATBED: { HEIGHT: 102 as number | null, WIDTH: 102 as number | null },
    STEP_DECK: { HEIGHT: 129 as number | null, WIDTH: 102 as number | null },
    OTHER: { HEIGHT: 110 as number | null, WIDTH: 102 as number | null },
} as const;

const inchesToCm = (inches: number | null) => (inches === null ? null : Math.floor(inches * 2.54));

const EQUIPMENT_DIMENSION_CAPS_CM = {
    STANDARD_VAN: {
        HEIGHT: inchesToCm(EQUIPMENT_DIMENSION_CAPS_INCHES.STANDARD_VAN.HEIGHT),
        WIDTH: inchesToCm(EQUIPMENT_DIMENSION_CAPS_INCHES.STANDARD_VAN.WIDTH),
    },
    FLATBED: {
        HEIGHT: inchesToCm(EQUIPMENT_DIMENSION_CAPS_INCHES.FLATBED.HEIGHT),
        WIDTH: inchesToCm(EQUIPMENT_DIMENSION_CAPS_INCHES.FLATBED.WIDTH),
    },
    STEP_DECK: {
        HEIGHT: inchesToCm(EQUIPMENT_DIMENSION_CAPS_INCHES.STEP_DECK.HEIGHT),
        WIDTH: inchesToCm(EQUIPMENT_DIMENSION_CAPS_INCHES.STEP_DECK.WIDTH),
    },
    OTHER: {
        HEIGHT: inchesToCm(EQUIPMENT_DIMENSION_CAPS_INCHES.OTHER.HEIGHT),
        WIDTH: inchesToCm(EQUIPMENT_DIMENSION_CAPS_INCHES.OTHER.WIDTH),
    },
} as const;

// Shared by dimension validators. Returns null when there is no cap for the given equipment type + field combination.
export const getDimensionMaxByEquipment = (field: 'HEIGHT' | 'WIDTH', equipmentType: string | null | undefined, weightMeasurement: string | null | undefined): number | null => {
    const isMetric = weightMeasurement === MEASUREMENT.WEIGHT2.value;
    const caps = isMetric ? EQUIPMENT_DIMENSION_CAPS_CM : EQUIPMENT_DIMENSION_CAPS_INCHES;

    if (DIMENSION_REQUIRED_EQUIPMENT_CODES.includes(equipmentType ?? '')) {
        return caps.STANDARD_VAN[field];
    }

    if (equipmentType === FLATBED_EQUIPMENT_CODE) {
        return caps.FLATBED[field];
    }

    if (STEP_DECK_EQUIPMENT_CODES.includes(equipmentType ?? '')) {
        return caps.STEP_DECK[field];
    }

    return caps.OTHER[field];
};

export const INTER_MODAL_TYPE_EQUIPMENT_TYPES = ['van', 'dry van', 'reefer'];
